import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Pencil, Trash2, RefreshCw } from 'lucide-react';
import type { Expense } from '../../types';
import { getCategoryMeta } from '../../hooks/useAnalytics';
import Badge from '../ui/Badge';
import Modal from '../ui/Modal';
import ExpenseForm from './ExpenseForm';
import toast from 'react-hot-toast';

interface ExpenseCardProps {
  expense: Expense;
  onUpdate: (id: string, data: Partial<import('../../types').ExpenseFormData>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

export default function ExpenseCard({ expense, onUpdate, onDelete }: ExpenseCardProps) {
  const [editOpen, setEditOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const meta = getCategoryMeta(expense.category || "");

  const handleDelete = async () => {
    if (!window.confirm('Delete this expense?')) return;
    setDeleting(true);
    try {
      await onDelete(expense.id);
      toast.success('Expense deleted');
    } catch {
      toast.error('Failed to delete expense');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="card-hover p-4 flex items-center gap-4 group animate-fade-in">
        {/* Category icon */}
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
          style={{ background: meta.bgColor }}
        >
          {meta.icon}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-slate-100 truncate">{expense.description}</p>
            {expense.recurring && (
              <Badge variant="info" className="flex items-center gap-1">
                <RefreshCw size={10} />
                Recurring
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-xs text-slate-500">
              {format(parseISO(expense.date), 'd MMM yyyy')}
            </span>
            <span className="text-xs text-slate-600">·</span>
            <span
              className="text-xs font-medium px-1.5 py-0.5 rounded-md"
              style={{ color: meta.color, background: meta.bgColor }}
            >
              {meta.label}
            </span>
          </div>
        </div>

        {/* Amount */}
        <p className="text-base font-semibold text-slate-100 flex-shrink-0">
          €{expense.amount.toFixed(2)}
        </p>

        {/* Actions — show on hover */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex-shrink-0">
          <button
            onClick={() => setEditOpen(true)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-brand-400 hover:bg-slate-800 transition-colors"
            aria-label="Edit expense"
          >
            <Pencil size={14} />
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-slate-800 transition-colors disabled:opacity-50"
            aria-label="Delete expense"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Edit Expense">
        <ExpenseForm
          expense={expense}
          onUpdate={onUpdate}
          onSuccess={() => setEditOpen(false)}
          onCancel={() => setEditOpen(false)}
        />
      </Modal>
    </>
  );
}
